import React, { Component } from "react";
import { i18n } from "../services/i18nService";
import { config } from "../config/config";
import { getSdk } from "../services/sdkService";
import { getLogger } from "../utils/logger";
import { isForceLogout } from "../utils/isForceLogout";
import { isUserAccountActive } from "../services/accountStatusService";
import { checkLanguage } from "../utils/checkLanguage";
import { isAnyAddonStatusActive } from "../utils/subscriptionUtils";
import themes from "../styles/themes/index";

let applicationState = {
  userLogged: false,
  isActiveUser: false,
  userLocale: null,
  parentalLevel: null,
  focusedCarouselIndex: 0,
  focusedCarouselItemIndex: 0,
  focusedMainMenuItemIndex: 0,
  focusedSubLayoutCarouselId: [],
  subLayoutCarouselItemIndex: [],
  focusedMediaControlItemIndex: -1,
  focusedFilterItemIndex: 0,
  layoutScrollTop: 0,
  theme: null,
  userAccountStatus: null,
  currentFocus: null,
  buildInfo: null,
  isOnline: true,
  appStarted: null,
  supports4K: false,
  userMediaCached: null,
  voucherData: null,
  userRelatedModules: null,
  list5NewsModules: null,
  addons: [],
  isSvodUser: false,
  requestedState: "",
  signupUsername: "",
  subscriptions: null,
  sCouponCode: null,
  userBilling: null,
  showPromotionPage: true,
  menuExpanded: false,
  lastFocusedName: "lists",
  paymentMethods: {},
  posterLandscapeImageForBuyAsset: "",
  selectedEpgCategoryIndex: 0,
  signupPromptActive: false,
  signupPromptTimer: null,
  isGuestProfile: false,
  currentProfileType: null,
  defaultLandingPath: "home",
  defaultActiveUserLandingPath: "home",
  userAddonStatus: false,
  activeUserRedirectionConfig: null,
  remoteProfileScreenBackGround: null,
  userSwitchPlaninfo: null,
  secLevelMenuPrevPath: null,
  userAccountFailed: false,
  remoteConfig: null,
  playbackStartTime: 0,
  playbackPausedTime: null,
  playbackStartSeconds: 0,
  isProfileDeletedModal: false,
  currentProfileId: "",
  showSponsorBugs: true,
  showPauseAds: true,
  globalConfig: null
};

const ApplicationState = React.createContext(applicationState);

/**
 * Provider component that uses the React Context API to make the global app state available for the child components
 * interested on reading or manipulating the state. This component should wrap all the components that need the state so
 * it should be one of the higher components on the application's component tree.
 */
export class StateProvider extends Component {
  constructor(props) {
    super(props);

    applicationState.userLocale = i18n.getLocale() || applicationState.userLocale || config.get().sdk.defaultLocale;
    applicationState.parentalLevel = getSdk().parentalLevel;

    this.userLoggedIn = this.userLoggedIn.bind(this);
    this.userLoggedOut = this.userLoggedOut.bind(this);
    this.setUserAccountStatus = this.setUserAccountStatus.bind(this);
    this.setAddons = this.setAddons.bind(this);
    this.setPlayer = this.setPlayer.bind(this);
    this.setPlayerConfig = this.setPlayerConfig.bind(this);
    this.setRequestedState = this.setRequestedState.bind(this);
    this.setPlayerTimerConfig = this.setPlayerTimerConfig.bind(this);
    this.setLocale = this.setLocale.bind(this);
    this.setSubscriptions = this.setSubscriptions.bind(this);
    this.setParentalLevel = this.setParentalLevel.bind(this);
    this.showGlobalModal = this.showGlobalModal.bind(this);
    this.hideGlobalModal = this.hideGlobalModal.bind(this);
    this.showSettingsModal = this.showSettingsModal.bind(this);
    this.hideSettingsModal = this.hideSettingsModal.bind(this);
    this.changeTheme = this.changeTheme.bind(this);
    this.setCurrentFocus = this.setCurrentFocus.bind(this);
    this.setAppState = this.setAppState.bind(this);
    this.setMenuItemFocus = this.setMenuItemFocus.bind(this);
    this.setMediaControlItemFocus = this.setMediaControlItemFocus.bind(this);
    this.setCarouselItemFocus = this.setCarouselItemFocus.bind(this);
    this.setCarouselFocus = this.setCarouselFocus.bind(this);
    this.setSubLayoutCarouselFocus = this.setSubLayoutCarouselFocus.bind(this);
    this.popSubLayoutCarouselFocus = this.popSubLayoutCarouselFocus.bind(this);
    this.setSubLayoutCarouselItemFocus = this.setSubLayoutCarouselItemFocus.bind(this);
    this.setPosterLandscapeImage = this.setPosterLandscapeImage.bind(this);
    this.applyKidsTheme = this.applyKidsTheme.bind(this);
    this.setAppStartedValue = this.setAppStartedValue.bind(this);
    this.setLayoutScrollTop = this.setLayoutScrollTop.bind(this);
    this.setUserBilling = this.setUserBilling.bind(this);
    this.setForceLogout = this.setForceLogout.bind(this);
    this.setLastFocusedName = this.setLastFocusedName.bind(this);
    this.setPaymentMethods = this.setPaymentMethods.bind(this);
    this.setAllMops = this.setAllMops.bind(this);
    this.setSelectedEpgCategoryIndex = this.setSelectedEpgCategoryIndex.bind(this);
    this.logger = getLogger();
    this.setSignupPrompt = this.setSignupPrompt.bind(this);
    this.setDefaultLandingPath = this.setDefaultLandingPath.bind(this);
    this.setUserAddonsStatus = this.setUserAddonsStatus.bind(this);
    this.setDefaultActiveUserLandingPath = this.setDefaultActiveUserLandingPath.bind(this);
    this.setWidevinePlayerConfig = this.setWidevinePlayerConfig.bind(this);
    this.setUserSwitchPlaninfo = this.setUserSwitchPlaninfo.bind(this);
    this.setSecLevelMenuPrevPath = this.setSecLevelMenuPrevPath.bind(this);
    this.setRemoteConfigContext = this.setRemoteConfigContext.bind(this);
    this.setUserAccountFailed = this.setUserAccountFailed.bind(this);
    this.setCurrentProfileId = this.setCurrentProfileId.bind(this);
    this.setGlobalConfig = this.setGlobalConfig.bind(this);
    this.setIsSvodUser = this.setIsSvodUser.bind(this);

    this.state = {
      applicationState,
      actions: {
        userLoggedIn: this.userLoggedIn,
        userLoggedOut: this.userLoggedOut,
        setUserAccountStatus: this.setUserAccountStatus,
        setIsUserAccountActive: this.setIsUserAccountActive,
        setAddons: this.setAddons,
        setPlayer: this.setPlayer,
        setPlayerConfig: this.setPlayerConfig,
        setRequestedState: this.setRequestedState,
        setPlayerTimerConfig: this.setPlayerTimerConfig,
        setLocale: this.setLocale,
        setSubscriptions: this.setSubscriptions,
        setParentalLevel: this.setParentalLevel,
        showGlobalModal: this.showGlobalModal,
        hideGlobalModal: this.hideGlobalModal,
        showSettingsModal: this.showSettingsModal,
        hideSettingsModal: this.hideSettingsModal,
        changeTheme: this.changeTheme,
        setCurrentFocus: this.setCurrentFocus,
        setMenuItemFocus: this.setMenuItemFocus,
        setCarouselItemFocus: this.setCarouselItemFocus,
        setCarouselFocus: this.setCarouselFocus,
        setSubLayoutCarouselFocus: this.setSubLayoutCarouselFocus,
        popSubLayoutCarouselFocus: this.popSubLayoutCarouselFocus,
        setSubLayoutCarouselItemFocus: this.setSubLayoutCarouselItemFocus,
        setPosterLandscapeImage: this.setPosterLandscapeImage,
        applyKidsTheme: this.applyKidsTheme,
        setAppStartedValue: this.setAppStartedValue,
        setLayoutScrollTop: this.setLayoutScrollTop,
        setForceLogout: this.setForceLogout,
        setUserBilling: this.setUserBilling,
        setMediaControlItemFocus: this.setMediaControlItemFocus,
        setLastFocusedName: this.setLastFocusedName,
        setPaymentMethods: this.setPaymentMethods,
        setAllMops: this.setAllMops,
        setSelectedEpgCategoryIndex: this.setSelectedEpgCategoryIndex,
        setSignupPrompt: this.setSignupPrompt,
        setDefaultLandingPath: this.setDefaultLandingPath,
        setUserAddonsStatus: this.setUserAddonsStatus,
        setDefaultActiveUserLandingPath: this.setDefaultActiveUserLandingPath,
        setWidevinePlayerConfig: this.setWidevinePlayerConfig,
        setUserAccountFailed: this.setUserAccountFailed,
        setGlobalConfig: this.setGlobalConfig,
        // Multiple Profiles
        setAllProfiles: this.setAllProfiles,
        setCurrentlyActiveProfile: this.setCurrentlyActiveProfile,
        setMasterProfile: this.setMasterProfile,
        setUserSwitchPlaninfo: this.setUserSwitchPlaninfo,
        setSecLevelMenuPrevPath: this.setSecLevelMenuPrevPath,
        setRemoteConfigContext: this.setRemoteConfigContext,
        setCurrentProfileId: this.setCurrentProfileId,
        setIsSvodUser: this.setIsSvodUser
      }
    };
    this.checkInitiallyRenderedGlobalModal();
    this.checkInitiallyRenderedSettingsModal();
  }

  setLastFocusedName(focusedName) {
    this.setAppState("lastFocusedName", focusedName);
  }

  setAppState(property, value, callback) {
    const newState = {
      ...this.state
    };
    newState.applicationState[property] = value;
    this.setState(newState, callback);
    applicationState = newState.applicationState;
  }

  userLoggedIn(userData, currentProfile = null) {
    const appLanguage = checkLanguage(userData.settings.language || userData.locale);
    this.setAppState("userLogged", true);
    this.setInitialParentalLevel(userData.settings.parentalControl);
    this.setLocale(currentProfile ? currentProfile.language : appLanguage);
    this.setUserAccountStatus(userData.settings.accountStatus);
    this.setIsUserAccountActive(userData.settings.accountStatus);
    this.setAddons(userData.settings.addons || []);
    this.setIsSvodUser(
      isAnyAddonStatusActive(userData.settings?.addons) || isUserAccountActive(userData.settings.accountStatus)
        ? true
        : false
    );
    this.setRequestedState(userData.settings.requestedState);
    this.setAppState("userRelatedModules", null);
    this.setAppState("list5NewsModules", null);
    this.setAppState("currentProfileType", userData?.profileType);
    this.setAppState("isGuestProfile", false);
    // Adtech Project - Clear States on LogIn - Start
    this.setAppState("showSponsorBugs", false);
    this.setAppState("showPauseAds", false);

    // Adtech Project - Clear States on LogIn - End
  }

  userLoggedOut() {
    this.setMenuItemFocus();
    this.setAppState("userLogged", false);
    this.resetParentalLevel();
    this.setUserAccountStatus(null);
    this.setAddons([]);
    this.setRequestedState("");
    this.setAppState("userRelatedModules", null);
    this.setAppState("list5NewsModules", null);
    this.setAppState("paymentMethods", []);
    this.setAppState("allMops", []);
    this.setAppState("userBilling", null);
    this.setUserAddonsStatus(false);
    this.setAppState("isActiveUser", false);
    this.setAppState("userSwitchPlaninfo", null);
    // Adtech Project - Clear States on LogOut - Start
    this.setAppState("showSponsorBugs", false);
    this.setAppState("showPauseAds", false);
    this.setAppState("isSvodUser", false);
    // Adtech Project - Clear States on LogOut - End
  }

  setUserAccountStatus(status) {
    this.setAppState("userAccountStatus", status);
  }

  setIsUserAccountActive(status) {
    const isActiveUser = isUserAccountActive(status);
    this.setAppState("isActiveUser", isActiveUser);
  }

  setUserAddonsStatus(status) {
    this.setAppState("userAddonStatus", status);
  }

  setSelectedEpgCategoryIndex(index) {
    this.setAppState("selectedEpgCategoryIndex", index);
  }

  setAddons(addons) {
    this.setAppState("addons", addons);
  }

  setUserBilling(billing) {
    this.setAppState("userBilling", billing);
  }

  setUserSwitchPlaninfo(billing) {
    this.setAppState("userSwitchPlaninfo", billing);
  }
  setSecLevelMenuPrevPath(pathName) {
    this.setAppState("secLevelMenuPrevPath", pathName);
  }
  setRemoteConfigContext(config) {
    this.setAppState("remoteConfig", config);
  }

  setPaymentMethods(paymentMethod) {
    this.setAppState("paymentMethods", { ...this.state.applicationState.paymentMethods, ...paymentMethod });
  }

  setAllMops(allMops) {
    this.setAppState("allMops", allMops);
  }

  setRequestedState(requestedState) {
    this.setAppState("requestedState", requestedState);
  }

  async setLocale(locale) {
    this.setAppState("userLocale", locale);
    const result = i18n.setLocale(locale);
    // if invalid token error set Force logout flag
    result && (await this.setForceLogout(result));
  }

  async setForceLogout(e) {
    const forceLogoutStatus = await isForceLogout(e);
    if (forceLogoutStatus === true) {
      this.setAppState("forceLogout", true);
    }
  }

  setSubscriptions(subscriptions) {
    this.setAppState("subscriptions", subscriptions);
  }

  setPlayer(player) {
    this.setAppState("player", player);
  }

  setPlayerConfig(config) {
    this.setAppState("playerConfig", config);
  }

  setPlayerTimerConfig(config) {
    this.setAppState("playertimers", config);
  }

  setWidevinePlayerConfig(config) {
    this.setAppState("widevineConfig", config);
  }

  setDefaultActiveUserLandingPath(path) {
    this.setAppState("defaultActiveUserLandingPath", path);
  }

  setCurrentFocus(focusPath) {
    // FIXME: Performance issue, pending refactor.
    // this.setAppState("currentFocus", focusPath);
    applicationState.currentFocus = focusPath;
  }

  setInitialParentalLevel(level) {
    getSdk().setInitialParentalLevel(level);
    this.setAppState("parentalLevel", level);
  }

  async setParentalLevel(level, password) {
    try {
      await getSdk().changeParentalLevel(level, password);
      this.setAppState("parentalLevel", level);
    } catch (e) {
      await this.setForceLogout(e);
      throw e;
    }
  }

  resetParentalLevel() {
    const parentalList = config.get().parentalControlList;
    getSdk().setInitialParentalLevel();
    this.setAppState("parentalLevel", parentalList[parentalList.length - 1]);
  }

  checkInitiallyRenderedGlobalModal() {
    this.state.applicationState.globalModal && this.handleGlobalModalConfig(this.state.applicationState.globalModal);
  }

  checkInitiallyRenderedSettingsModal() {
    this.state.applicationState.settingsModal &&
      this.handleSettingsModalConfig(this.state.applicationState.settingsModal);
  }

  setMenuItemFocus(index = 0) {
    this.setAppState("focusedMainMenuItemIndex", index);
  }
  setMediaControlItemFocus(index = 0) {
    this.setAppState("focusedMediaControlItemIndex", index);
  }

  setCarouselItemFocus(index = 0) {
    // FIXME: Performance issue, pending refactor.
    // this.setAppState("focusedCarouselItemIndex", index);
    applicationState.focusedCarouselItemIndex = index;
  }

  setLayoutScrollTop(scrollTop = 0) {
    // FIXME: Performance issue, pending refactor.
    // this.setAppState("layoutScrollTop", index);
    applicationState.layoutScrollTop = scrollTop;
  }

  setCarouselFocus(index = 0) {
    // FIXME: Performance issue, pending refactor.
    // this.setAppState("focusedCarouselIndex", index);
    applicationState.focusedCarouselIndex = index;
  }

  setSubLayoutCarouselFocus(id) {
    applicationState.focusedSubLayoutCarouselId.push(id);
  }

  popSubLayoutCarouselFocus() {
    const carouselArr = applicationState.focusedSubLayoutCarouselId || [];
    const carouselItemArr = applicationState.subLayoutCarouselItemIndex || [];
    if (carouselArr && carouselArr.length && carouselItemArr && carouselItemArr.length) {
      const carouselNewArr = carouselArr.slice(0, -1);
      const carouselItemNewArr = carouselItemArr.slice(0, -1);
      this.setAppState("focusedSubLayoutCarouselId", carouselNewArr);
      this.setAppState("subLayoutCarouselItemIndex", carouselItemNewArr);
    }
  }

  setSubLayoutCarouselItemFocus(index = 0) {
    applicationState.subLayoutCarouselItemIndex.push(index);
  }

  setPosterLandscapeImage(url) {
    applicationState.posterLandscapeImageForBuyAsset = url;
  }

  applyKidsTheme() {
    getAppState().parentalLevel === "1" && this.changeTheme("kids");
  }

  setAppStartedValue() {
    this.setAppState("appStarted", true);
  }

  handleGlobalModalConfig(config) {
    if (config && config.dismissable !== false) {
      config.dismiss = this.hideGlobalModal;
    }
    if (config.onShowListener) {
      config.onShowListener();
    }
  }

  handleSettingsModalConfig(config) {
    if (config && config.dismissable !== false) {
      config.dismiss = this.hideSettingsModal;
    }
    if (config.onShowListener) {
      config.onShowListener();
    }
  }

  showGlobalModal(config) {
    this.handleGlobalModalConfig(config);
    this.setAppState("globalModal", config);

    return true;
  }

  showSettingsModal(config) {
    this.handleSettingsModalConfig(config);
    this.setAppState("settingsModal", config);
  }

  hideGlobalModal(callback) {
    const { globalModal: config } = applicationState;
    if (config && config.onHideListener) {
      config.onHideListener();
    }
    this.setAppState("globalModal", null);
    callback && callback();
    // Returns true to match de global onBack behaviour (App.js).
    return true;
  }

  hideSettingsModal(callback) {
    const { settingsModal: config } = applicationState;
    if (config && config.onHideListener) {
      config.onHideListener();
    }
    this.setAppState("settingsModal", null);
    callback && callback();
    // Returns true to match de global onBack behaviour (App.js).
    return true;
  }

  changeTheme(themeName) {
    //this.setAppState("theme", themes[themeName]);
    this.setAppState("theme", themes[themeName]);
  }

  setSignupPrompt(status) {
    this.setAppState("signupPromptActive", status);
  }

  setDefaultLandingPath(path) {
    this.setAppState("defaultLandingPath", path);
  }
  setDefaultActiveUserLandingPath(path) {
    this.setAppState("defaultActiveUserLandingPath", path);
  }

  setUserAccountFailed(status) {
    this.setAppState("userAccountFailed", status);
  }

  setCurrentProfileId(id) {
    this.setAppState("currentProfileId", id);
  }

  setGlobalConfig(config) {
    this.setAppState("globalConfig", config);
  }
  setIsSvodUser(status) {
    this.setAppState("isSvodUser", status);
  }

  shouldComponentUpdate(nextProps, nextState) {
    // FIXME: AppState complete refactor to make it re-render only when required.
    return true;
    // return (
    //   this.state.userLogged !== nextState.userLogged ||
    //   this.state.userLocale !== nextState.userLocale ||
    //   this.state.parentalLevel !== nextState.parentalLevel ||
    //   this.state.theme !== nextState.theme ||
    //   this.state.userAccountStatus !== nextState.userAccountStatus ||
    //   this.state.globalModal !== nextState.globalModal
    // );
  }

  render() {
    return <ApplicationState.Provider value={this.state}>{this.props.children}</ApplicationState.Provider>;
  }
}

/**
 * Higher order component that becomes it's wrapped components aware of the state. Those components that are wrapped by
 * withAppState will receive an extra prop called applicationState that will be an object representing the current
 * globalstate of the app.
 * @param {Component} Child The component that needs to receive the state as an extra prop.
 * @returns {Component} A react component that is the same as Child but will receive an extra prop called
 * applicationState
 */
export const withAppState = Child => {
  return class extends Component {
    constructor(props) {
      super(props);
      Child.defaultFocusPath && (this.defaultFocusPath = Child.defaultFocusPath);
      Child.name && (this.name = Child.name);
    }
    render() {
      return (
        <ApplicationState.Consumer>
          {state => <Child {...this.props} applicationState={state.applicationState} stateActions={state.actions} />}
        </ApplicationState.Consumer>
      );
    }
  };
};

export function getAppState() {
  return applicationState;
}
